import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { EMPTY } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { AccountConversationsBackend } from '@libs/src/account/account-conversations.backend';
import { ToastService } from '@libs/src/toast/toast.service';

import { Account } from '@libs/src/models/account.model';
import { Profile } from '@main-client/src/app/profile/profile';
import { ProfileMessageDialogConfig } from '@main-client/src/app/profile/profile-message-dialog/profile-message-dialog';

import { ProfileMessageStatus } from '../../app.state';

import isEqual from 'lodash-es/isEqual';

@Component({
  selector: 'app-profile-message-dialog',
  templateUrl: './profile-message-dialog.component.html',
})
export class ProfileMessageDialogComponent implements OnInit {
  profile: Profile;
  user: Account;
  messagingForm: UntypedFormControl = this.fb.control('', Validators.required);
  isLoading = false;
  initialMessage: string;
  showUnsavedChangesPrompt = false;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: ProfileMessageDialogConfig,
    public accountConversationsBackend: AccountConversationsBackend,
    public dialogRef: MatDialogRef<ProfileMessageDialogComponent>,
    public fb: UntypedFormBuilder,
    public router: Router,
    public toastService: ToastService,
  ) {}

  ngOnInit() {
    this.user = this.data.user;
    this.profile = this.data.profile;
    this.initialMessage =
      this.data.message ||
      this.getMessageTemplate(this.user.first_name, this.profile.first_name);
    this.messagingForm.setValue(this.initialMessage);
  }

  sendMessage(form: UntypedFormControl) {
    if (!form.valid) {
      return;
    }
    this.isLoading = true;
    const message = form.value;
    const conversationOperation = this.profile?.conversationId
      ? this.addNewMessage(message)
      : this.createConversation(message);
    conversationOperation
      .pipe(
        tap((response: { _id: string }) => {
          this.router.navigate(['/conversations', response._id]);
          this.dialogRef.close(ProfileMessageStatus.sent);
          this.isLoading = false;
        }),
        catchError((responseError: HttpErrorResponse) => {
          this.toastService.showError(responseError.error.message);
          this.isLoading = false;
          return EMPTY;
        }),
      )
      .subscribe();
  }

  createConversation(message: string) {
    const participants = [this.user, this.profile].map((participant) => ({
      account: participant.slug as string,
      role: 'expert',
    }));
    return this.accountConversationsBackend.v2.post(
      message,
      participants,
      !!this.profile.match,
      this.data.origin,
    );
  }

  addNewMessage(message: string) {
    return this.accountConversationsBackend.v1.postSubmessages(
      this.profile.conversationId,
      message,
    );
  }

  getMessageTemplate(senderName: string, receiverName: string) {
    return $localize`:@@profileView.messageDialog.defaultMessage.option2:Hi ${receiverName}, I'm ${senderName}.  I'm interested in scheduling a virtual chat. Is there a time in the next few weeks that works for you?`;
  }

  checkForUnsavedChanges() {
    const hasUnsavedChanges = !isEqual(
      this.initialMessage,
      this.messagingForm.value,
    );
    if (!hasUnsavedChanges) {
      return this.dialogRef.close();
    }
    this.showUnsavedChangesPrompt = true;
  }

  showMessageDialog() {
    this.showUnsavedChangesPrompt = false;
  }
}
